/**
 * Production-only visibility hiding — CSS + router-guard approach (no
 * per-component conditionals). Env detection matches the existing
 * `AUTH_CLIENT_METADATA` / `resolveLinkedEmployeeEnv` convention
 * (`src/api/authClientMetadata.ts`, `src/api/services/iamUsers.ts`):
 * `MODE === 'production'` → production, everything else (dev, `vitest`
 * running MODE='test') → development.
 *
 * `PROD_HIDDEN_ROUTES` names are consumed two ways:
 *   1. `installAuthGuard` (`src/router/guards.ts`) redirects to
 *      `DEFAULT_LANDING_ROUTE` if a hidden route is reached directly by URL.
 *   2. `src/config/prod-hidden.css` hides the corresponding nav
 *      pill / sub-nav tab via its existing `data-testid`, scoped under
 *      the `html[data-env="production"]` attribute stamped once in
 *      `src/main.ts`.
 *
 * This only hides navigation entry points — it does not stop the route's
 * component from rendering if reached some other way, and it does not
 * remove anything from the shipped bundle. Content that must actually
 * change (different data, not just visibility) is handled in the
 * component itself, not here.
 */
/**
 * Whether a Vite build mode is the production build.
 * @param mode - The build mode (default: `import.meta.env.MODE`).
 * @returns `true` only for `'production'`.
 */
export function isProductionMode(mode: string = import.meta.env.MODE): boolean {
  return mode === 'production';
}

export const IS_PRODUCTION = isProductionMode();

/**
 * Whether mock / demo data and the demo-only controls are on for a build mode. Off in a
 * production build, on everywhere else (dev, staging builds run with another mode, vitest).
 * Every module `*_DEMO` flag (`PAY_DEMO`, `WORK_DEMO`, `BILLING_DEMO`, `FO_DEMO`) reads
 * {@link DEMO_ENABLED} — mock data never reaches a production build.
 * @param mode - The build mode (default: `import.meta.env.MODE`).
 * @returns `false` for `'production'`, otherwise `true`.
 */
export function isDemoEnabled(mode: string = import.meta.env.MODE): boolean {
  return !isProductionMode(mode);
}

/** The build-mode demo switch shared by every module `*_DEMO` flag. */
export const DEMO_ENABLED = isDemoEnabled();

/**
 * Every Pay route name. Pay runs on mock data only (`PAY_DEMO`), so a production build hides
 * the whole module until its V2 repository lands.
 */
export const PAY_ROUTE_NAMES = [
  'pay',
  'pay-run',
  'pay-run-step-4',
  'pay-run-detail',
  'pay-run-payslips',
  'pay-run-payslip',
  'pay-run-step-5',
  'pay-templates',
  'pay-input-center',
  'pay-reports',
  'pay-off-cycle',
  'pay-off-cycle-draft',
] as const;

export const PROD_HIDDEN_ROUTES = [
  'home',
  'billing',
  'perform',
  'fieldops',
  'workmgmt',
  'insights',
  // HR Ops runs on mock data only (`WORK_DEMO`).
  'work',
  ...PAY_ROUTE_NAMES,
  // Admin › Setup › Cost Centers (round 3, W4) — V2-only (the V2 cost-center
  // entity has no V1 counterpart in the production web), so production keeps
  // it hidden until the operator promotes the V2 admin screens.
  'admin-cost-centers',
] as const;

export type ProdHiddenRoute = (typeof PROD_HIDDEN_ROUTES)[number];

export function isRouteHiddenInProd(routeName: string | null | undefined): boolean {
  if (!IS_PRODUCTION || !routeName) return false;
  return (PROD_HIDDEN_ROUTES as readonly string[]).includes(routeName);
}

/**
 * Home is hidden in production (see PROD_HIDDEN_ROUTES above), so it can't
 * be the universal default landing page. 'people' takes over as the
 * fallback destination in production only — dev/staging keep landing on
 * Home. Used for the '/' redirect, the 404 catch-all, the guard's
 * deny-access redirect, and the post-sign-in fallback (no `?redirect=`
 * carried over from a protected-route bounce).
 */
export const DEFAULT_LANDING_ROUTE: 'home' | 'people' = IS_PRODUCTION ? 'people' : 'home';
